// Composable for managing stored user GUID via localStorage
// Used for PWA deep linking to /auth/login/{uuid}
// Stored in localStorage so it persists when app is installed on phone

import { ref, computed } from 'vue';

const USER_GUID_STORAGE_KEY = 'userGuid';

export const useStoredUserGuid = () => {
  const userGuid = ref<string | null>(null);
  const isLoading = ref(false);

  // Load user GUID from localStorage
  const loadUserGuid = async (): Promise<string | null> => {
    isLoading.value = true;
    try {
      const guid = localStorage.getItem(USER_GUID_STORAGE_KEY);
      userGuid.value = guid;
      return guid;
    } catch (error) {
      console.error('[useStoredUserGuid] Error loading user GUID:', error);
      userGuid.value = null;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const setUserGuid = async (guid: string): Promise<void> => {
    try {
      localStorage.setItem(USER_GUID_STORAGE_KEY, guid);
      userGuid.value = guid;
    } catch (error) {
      console.error('[useStoredUserGuid] Error storing user GUID:', error);
      userGuid.value = guid;
    }
  };

  const getUserGuid = async (): Promise<string | null> => {
    if (userGuid.value !== null) {
      return userGuid.value;
    }
    return await loadUserGuid();
  };

  const clearUserGuid = async (): Promise<void> => {
    try {
      localStorage.removeItem(USER_GUID_STORAGE_KEY);
      userGuid.value = null;
    } catch (error) {
      console.error('[useStoredUserGuid] Error clearing user GUID:', error);
      userGuid.value = null;
    }
  };

  // Initialize on first use
  loadUserGuid();

  return {
    userGuid: computed(() => userGuid.value),
    isLoading: computed(() => isLoading.value),
    setUserGuid,
    getUserGuid,
    clearUserGuid,
    loadUserGuid,
  };
};
